'use client';

import { useLenis } from 'lenis/react';
import { useMemo } from 'react';
import {
  Gallery as PhotoswipeGallery,
  GalleryProps,
  Item as PhotoswipeItem,
} from 'react-photoswipe-gallery';

import Wrap from '~/components/atoms/Wrap';
import AutoPlayVideo from '~/components/molecules/AutoPlayVideo';
import PropertyGalleryItem from '~/components/organisms/property/PropertyGalleryItem';
import { GalleryMediaSchema } from '~/lib/api/fragments/gallery-media';
import { useHydrationSafeResponsive } from '~/lib/hooks/useResponsive';
import { cn } from '~/lib/utils/classname';

interface Props extends React.JSX.IntrinsicAttributes {
  media?: GalleryMediaSchema[] | null;
  preview?: boolean;
}

const PropertyGallery: React.FC<Props> = ({ media, preview, ...rest }) => {
  const isDesktop = useHydrationSafeResponsive('desktop');
  const isPointerCoarse = useHydrationSafeResponsive('pointer-coarse');
  const lenis = useLenis();

  const options = useMemo<GalleryProps['options']>(
    () => ({
      zoom: false,
      counter: false,
      bgOpacity: 0.8,
      padding: { top: 16, bottom: 16, left: 0, right: 0 },
      closeTitle: 'Fermer',
      zoomTitle: 'Zoom',
      arrowPrevTitle: 'Aller à la photo précédente',
      arrowNextTitle: 'Aller à la photo suivante',

      closeSVG: /* svg */ `
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <path stroke="currentColor" d="m8 8 4 4m4 4-4-4m0 0-4 4m4-4 4-4"/>
        </svg>
      `,

      arrowPrevSVG: /* svg */ `
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
          <path fill="currentColor" d="m10 12 4-3v6l-4-3Z"/>
        </svg>
      `,

      arrowNextSVG: /* svg */ `
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
          <path fill="currentColor" d="m14 12-4 3V9l4 3Z"/>
        </svg>
      `,
    }),
    []
  );

  if (!media || media.length === 0) {
    return null;
  }

  const columnsCount = Math.ceil(media.length / 3);

  return (
    <section
      className={cn('relative py-10 lg:py-[50px] lg:px-24')}
      {...rest}
    >
      <div
        className={cn(
          'relative grid gap-2 max-lg:grid-cols-3 lg:h-full lg:auto-cols-auto lg:grid-flow-col lg:grid-rows-3 lg:gap-20',
          {
            '[@media(max-height:580px)]:gap-5! [@media(max-height:800px)]:gap-10':
              isDesktop && !preview,
          }
        )}
        style={
          isDesktop
            ? {
                /* 🤯😔 */
                width: `calc(((100svh - (72px + 8px + 50px * 2 + 80px * 2)) / 3) * (120 / 180) * ${columnsCount} + 80px * ${columnsCount - 1})`,
              }
            : undefined
        }
      >
        <Wrap
          with={(children) => (
            <PhotoswipeGallery
              options={options}
              onBeforeOpen={(photoswipe) => {
                photoswipe.on('close', () => {
                  if (lenis && isPointerCoarse) {
                    lenis.start();
                  }
                });
              }}
              onOpen={(photoswipe) => {
                if (lenis && isPointerCoarse) {
                  lenis.stop();
                }

                if (photoswipe.currSlide) {
                  const video =
                    photoswipe.currSlide.container.querySelector('video');

                  if (video) {
                    video.currentTime = 0;
                    video.play();
                  }
                }
              }}
            >
              {children}
            </PhotoswipeGallery>
          )}
          when={!preview}
        >
          {media.map((media, index) => {
            if (preview) {
              return <PropertyGalleryItem key={index} media={media} />;
            }

            if (media.node.mediaType === 'image') {
              const thumbnailUrl: string =
                (media.node.mediaDetails.sizes || []).find(
                  (size) => size.name === 'medium'
                )?.sourceUrl || media.node.sourceUrl;

              return (
                <PhotoswipeItem
                  key={index}
                  original={media.node.sourceUrl}
                  thumbnail={thumbnailUrl}
                  width={media.node.mediaDetails.width}
                  height={media.node.mediaDetails.height}
                  cropped
                >
                  {({ ref, open }) =>
                    media.node.mediaType === 'image' && (
                      <PropertyGalleryItem
                        key={index}
                        media={media}
                        onClick={open}
                        ref={ref}
                      />
                    )
                  }
                </PhotoswipeItem>
              );
            } else {
              return (
                <PhotoswipeItem
                  key={index}
                  original={media.node.mediaItemUrl}
                  thumbnail={media.node.mediaItemUrl}
                  width={media.node.mediaDetails.width}
                  height={media.node.mediaDetails.height}
                  content={
                    <div className="flex px-4">
                      <AutoPlayVideo
                        src={media.node.mediaItemUrl}
                        type={media.node.mimeType}
                      />
                    </div>
                  }
                >
                  {({ ref, open }) => (
                    <PropertyGalleryItem
                      key={index}
                      media={media}
                      onClick={open}
                      ref={ref}
                    />
                  )}
                </PhotoswipeItem>
              );
            }
          })}
        </Wrap>
      </div>
    </section>
  );
};

export default PropertyGallery;
